import React, { FC, useContext, useEffect, useMemo, useRef, useState, memo, forwardRef } from 'react'
import { useSearchParams } from 'next/navigation'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { Tables } from '@/supabase/types'
import { getMessagesByChatId } from '@/db/messages'

import { CodeBlock } from '@/types/chat-message'
import { ChatMessage } from '@/types'
import type { ChatSendOptions } from '@/types/chat'
import { ExtendedMessage } from '@/types/messages'
import {
  CHAT_HIGHLIGHT_OPEN_EVENT,
  type ChatHighlight,
  type ChatHighlightDraft,
  type ChatHighlightOpenDetail
} from '@/lib/chat/chat-highlights'
import { getLatestPendingAssistantTurnState } from '@/lib/chat/pending-assistant-turn'
import {
  getRegenerateMessage,
  getPreviousUserMessageContent,
  getRegenerateSendOptions,
  type RegenerateRequest
} from '@/lib/chat/regenerate-options'
import {
  areChatMessagesInThreadOrder,
  getChatMessageRenderKey,
  mergeChatMessagesById,
  sortChatMessagesByThreadOrder
} from '@/lib/chat/message-order'
import { parseDBMessageCodeBlocksAndContent } from '@/lib/messages'
import { useChatHandler } from '@/components/chat/chat-hooks/use-chat-handler'
import { hasAssistantTurnOutput } from '@/components/chat/chat-helpers'
import type { ReactionTargetType } from '@/components/reactions/reaction-target'

import { Message } from '../messages/message'
import { MessageErrorBoundary } from '../messages/message-error-boundary'
import { useScroll } from './chat-hooks/use-scroll'
import { Button } from '@/components/ui/button'
import {
  IconChevronUp,
  IconLoader2
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

interface ChatMessagesProps {
  onSelectCodeBlock?: (codeBlock: CodeBlock | null) => void
  onSelectMessage?: (message: any) => void
  isExperimentalCodeEditor: boolean
  isPublic?: boolean // Hide certain actions in public chat
  /** Narrow Sidekick panel: suppress own-user right-align. Leave unset for Space/main chat. */
  isSidekick?: boolean
  chatId?: string
  reactionTargetType?: ReactionTargetType | null
  onRegenerateMessage?: (
    message: string,
    currentMessages: ChatMessage[],
    options?: ChatSendOptions
  ) => void | Promise<void>
  onConfirmInteractiveMessage?: (
    payload: string,
    currentMessages: ChatMessage[]
  ) => void | Promise<void>
  chatHighlights?: ChatHighlight[]
  onCreateHighlight?: (highlight: ChatHighlightDraft) => void | Promise<void>
  /**
   * Owned by the parent scroller's useScroll. Pins through the programmatic
   * scroll lock so pin-to-top does not mark userScrolled and block settle.
   */
  pinNewestMessageToTop?: (el: HTMLElement | null | undefined) => void
}

// Constants for message pagination
const INITIAL_MESSAGE_LIMIT = 50 // Show last 50 messages initially
const LOAD_MORE_BATCH_SIZE = 30 // Load 30 more messages when "Load More" is clicked
const EMPTY_FILE_ITEMS: Tables<'file_items'>[] = []
const EMPTY_HIGHLIGHTS: ChatHighlight[] = []

type MessageRowWithFileItems = Tables<'messages'> & {
  file_items?: Tables<'file_items'>[]
}

function areMessageImagePathsEqual(
  previousMessage: { image_paths?: unknown },
  nextMessage: { image_paths?: unknown }
) {
  const previousPaths = previousMessage.image_paths
  const nextPaths = nextMessage.image_paths

  if (previousPaths === nextPaths) return true
  if (!Array.isArray(previousPaths) || !Array.isArray(nextPaths)) {
    return !Array.isArray(previousPaths) && !Array.isArray(nextPaths)
  }

  if (previousPaths.length !== nextPaths.length) return false

  for (let index = 0; index < previousPaths.length; index += 1) {
    if (previousPaths[index] !== nextPaths[index]) return false
  }

  return true
}

function areHighlightsEqual(
  previousHighlights?: ChatHighlight[],
  nextHighlights?: ChatHighlight[]
) {
  if (previousHighlights === nextHighlights) return true
  if (!previousHighlights || !nextHighlights) {
    return !previousHighlights && !nextHighlights
  }

  if (previousHighlights.length !== nextHighlights.length) return false

  for (let index = 0; index < previousHighlights.length; index += 1) {
    const previousHighlight = previousHighlights[index]
    const nextHighlight = nextHighlights[index]

    if (
      previousHighlight.id !== nextHighlight.id ||
      previousHighlight.text !== nextHighlight.text
    ) {
      return false
    }
  }

  return true
}

function mergeFileItemsById(
  currentFileItems: Tables<'file_items'>[],
  nextFileItems: Tables<'file_items'>[]
) {
  const mergedFileItems = [...currentFileItems]
  const seenFileItemIds = new Set(currentFileItems.map((fileItem) => fileItem.id))

  for (const fileItem of nextFileItems) {
    if (seenFileItemIds.has(fileItem.id)) continue

    seenFileItemIds.add(fileItem.id)
    mergedFileItems.push(fileItem)
  }

  return mergedFileItems
}

// Memoize the Message component to prevent unnecessary re-renders
const MessageWithBoundary = forwardRef<HTMLDivElement, React.ComponentProps<typeof Message>>(
  function MessageWithBoundary(props, ref) {
    // Isolate each message's render so a transient DOM/reconciliation error in one
    // message (e.g. during the stream->final transition) cannot cascade and take
    // down the whole message list.
    return (
      <MessageErrorBoundary
        resetKey={`${props.message?.id ?? ''}:${props.message?.content ?? ''}`}
        fallbackContent={typeof props.message?.content === 'string' ? props.message.content : ''}
      >
        <Message {...props} ref={ref} />
      </MessageErrorBoundary>
    )
  }
)
const MemoizedMessage = memo(MessageWithBoundary, (prevProps, nextProps) => {
  // Include annotation so tool results / losi_steps show during generation before first token
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    areMessageImagePathsEqual(prevProps.message, nextProps.message) &&
    prevProps.message.annotation === nextProps.message.annotation &&
    prevProps.message.metadata === nextProps.message.metadata &&
    prevProps.assistants === nextProps.assistants &&
    prevProps.profile === nextProps.profile &&
    prevProps.allModels === nextProps.allModels &&
    prevProps.selectedAssistant === nextProps.selectedAssistant &&
    prevProps.files === nextProps.files &&
    prevProps.fileItems === nextProps.fileItems &&
    prevProps.isEditing === nextProps.isEditing &&
    prevProps.isLast === nextProps.isLast &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isGenerating === nextProps.isGenerating &&
    prevProps.firstTokenReceived === nextProps.firstTokenReceived &&
    prevProps.isActiveAssistantTurn === nextProps.isActiveAssistantTurn &&
    prevProps.highlightQuoteText === nextProps.highlightQuoteText &&
    areHighlightsEqual(prevProps.highlights, nextProps.highlights) &&
    prevProps.reactionTargetType === nextProps.reactionTargetType
  )
})

export const ChatMessages: FC<ChatMessagesProps> = ({
  onSelectCodeBlock,
  onSelectMessage,
  isExperimentalCodeEditor,
  isPublic = false,
  isSidekick = false,
  chatId,
  reactionTargetType,
  onRegenerateMessage,
  onConfirmInteractiveMessage,
  chatHighlights = EMPTY_HIGHLIGHTS,
  onCreateHighlight,
  pinNewestMessageToTop
}) => {
  const { assistants, profile, allModels, selectedAssistant, files } =
    useContext(ChatbotUIContext)
  const {
    chatMessages: contextChatMessages,
    setChatMessages: setContextChatMessages,
    chatFileItems: contextChatFileItems,
    setChatFileItems: setContextChatFileItems,
    isGenerating: contextIsGenerating,
    setIsGenerating: setContextIsGenerating,
    firstTokenReceived: contextFirstTokenReceived,
    selectedMessages,
    isSelecting,
    handleMessageSelect,
    selectedChat,
    chatSettings,
    getChatStoreKey,
    getScopedChatBindings
  } = useContext(ChatbotUIChatContext)
  const scopedChatId = chatId || selectedChat?.id || null
  const scopedChatBindings =
    scopedChatId ? getScopedChatBindings(getChatStoreKey(scopedChatId)) : null
  const scopedChatState = scopedChatBindings?.getSnapshot() ?? null
  const chatMessages = scopedChatState?.chatMessages ?? contextChatMessages
  const chatFileItems = scopedChatState?.chatFileItems ?? contextChatFileItems
  const setChatMessages =
    scopedChatBindings?.setChatMessages ?? setContextChatMessages
  const setChatFileItems =
    scopedChatBindings?.setChatFileItems ?? setContextChatFileItems
  const isGenerating = scopedChatState?.isGenerating ?? contextIsGenerating
  const setIsGenerating =
    scopedChatBindings?.setIsGenerating ?? setContextIsGenerating
  const firstTokenReceived =
    scopedChatState?.firstTokenReceived ?? contextFirstTokenReceived
  const resolvedReactionTargetType =
    reactionTargetType === undefined
      ? selectedChat?.is_group_chat
        ? 'message'
        : null
      : reactionTargetType
  const lastUserMessageRef = useRef<HTMLDivElement>(null)
  const activeAssistantMessageRef = useRef<HTMLDivElement>(null)
  const previousLastUserMessageIdRef = useRef<string | null>(null)
  const isFirstRender = useRef<boolean>(true)

  const { t } = useTranslation()
  const searchParams = useSearchParams()
  const highlightedMessageId = searchParams?.get('highlight')
  const highlightedMessageRef = useRef<HTMLDivElement>(null)
  const [highlightQuoteText, setHighlightQuoteText] = useState<string | null>(null)

  // When navigating to a highlighted message from a quote click, read quoted text from sessionStorage
  useEffect(() => {
    if (!highlightedMessageId || typeof window === 'undefined') return
    try {
      const raw = sessionStorage.getItem('highlight-quote')
      if (!raw) return
      const data = JSON.parse(raw) as { messageId: string; text: string }
      if (data.messageId === highlightedMessageId && data.text) {
        setHighlightQuoteText(data.text)
        sessionStorage.removeItem('highlight-quote')
      }
    } catch {
      setHighlightQuoteText(null)
    }
  }, [highlightedMessageId])

  // Highlight-only scroll helpers. Pin-to-top MUST use the parent scroller's
  // pinNewestMessageToTop — a second useScroll here has no scrollRef attached,
  // so bare scrollIntoView would fight the parent's programmatic scroll lock.
  const { scrollToMessage, setShouldScrollToHighlight } = useScroll()

  // State for message limiting
  const [visibleMessageCount, setVisibleMessageCount] = useState(INITIAL_MESSAGE_LIMIT)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [exhaustedOldestCursor, setExhaustedOldestCursor] = useState<string | null>(null)

  const sortedMessages = useMemo(() => {
    if (!chatMessages || chatMessages.length === 0) return []
    if (areChatMessagesInThreadOrder(chatMessages)) return chatMessages

    return sortChatMessagesByThreadOrder(chatMessages)
  }, [chatMessages])
  // createTempMessages appends user+assistant in one update, so the newest
  // role is often "assistant". Key pin off the latest USER id so send still
  // animates/scrolls the user bubble into place.
  const lastUserMessageId = useMemo(() => {
    for (let index = sortedMessages.length - 1; index >= 0; index -= 1) {
      if (sortedMessages[index]?.message.role === 'user') {
        return sortedMessages[index].message.id
      }
    }
    return null
  }, [sortedMessages])

  // Pin-to-top: when the user sends, pin THEIR message near the top so the
  // reply has room below. Do NOT re-pin when only the assistant bubble
  // changes — ONE-SHOT per user message id so streaming never re-scrolls.
  // This pin is the only autoscroll: nothing settles to bottom afterwards.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      previousLastUserMessageIdRef.current = lastUserMessageId
      return
    }

    if (
      !lastUserMessageId ||
      previousLastUserMessageIdRef.current === lastUserMessageId
    ) {
      return
    }
    previousLastUserMessageIdRef.current = lastUserMessageId

    if (!pinNewestMessageToTop) return

    let attempts = 0
    let timeoutId: number | null = null
    const tryPin = () => {
      const el = lastUserMessageRef.current
      if (el) {
        pinNewestMessageToTop(el)
        return
      }
      attempts += 1
      if (attempts >= 8) return
      timeoutId = window.setTimeout(tryPin, 50)
    }
    timeoutId = window.setTimeout(tryPin, 40)

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [lastUserMessageId, pinNewestMessageToTop])

  const pendingAssistantTurn = useMemo(
    () => getLatestPendingAssistantTurnState(sortedMessages),
    [sortedMessages]
  )
  const pendingAssistantMessageId =
    pendingAssistantTurn?.message.message.id ?? null

  const localHasMoreMessages = sortedMessages.length > visibleMessageCount
  const olderMessageCount = Math.max(sortedMessages.length - visibleMessageCount, 0)
  const oldestLoadedCreatedAt = sortedMessages[0]?.message.created_at || null
  const canFetchRemoteOlderMessages = Boolean(
    scopedChatId &&
      oldestLoadedCreatedAt &&
      oldestLoadedCreatedAt !== exhaustedOldestCursor &&
      sortedMessages.length >= INITIAL_MESSAGE_LIMIT
  )
  const hasMoreMessages = localHasMoreMessages || canFetchRemoteOlderMessages
  const displayedOlderMessageCount =
    olderMessageCount > 0 ? olderMessageCount : LOAD_MORE_BATCH_SIZE

  const visibleMessages = useMemo(() => {
    const pageMessages = localHasMoreMessages
      ? sortedMessages.slice(-visibleMessageCount)
      : sortedMessages

    return pageMessages.filter((message, index, array) => {
      if (message.message.role !== 'assistant') return true
      if (message.message.id === pendingAssistantMessageId) return true
      if (isGenerating && index === array.length - 1) return true

      return hasAssistantTurnOutput(message)
    })
  }, [
    isGenerating,
    localHasMoreMessages,
    pendingAssistantMessageId,
    sortedMessages,
    visibleMessageCount
  ])

  const chatFileItemsById = useMemo(() => {
    const byId = new Map<string, Tables<'file_items'>>()

    for (const fileItem of chatFileItems) {
      if (!byId.has(fileItem.id)) {
        byId.set(fileItem.id, fileItem)
      }
    }

    return byId
  }, [chatFileItems])

  const visibleMessageFileItems = useMemo(() => {
    const byMessageId = new Map<string, Tables<'file_items'>[]>()

    for (const chatMessage of visibleMessages) {
      const items: Tables<'file_items'>[] = []
      const seenIds = new Set<string>()

      for (const fileItemId of chatMessage.fileItems) {
        if (seenIds.has(fileItemId)) continue

        const fileItem = chatFileItemsById.get(fileItemId)
        if (fileItem) {
          items.push(fileItem)
          seenIds.add(fileItemId)
        }
      }

      byMessageId.set(chatMessage.message.id, items)
    }

    return byMessageId
  }, [chatFileItemsById, visibleMessages])

  const highlightsByMessageId = useMemo(() => {
    const byMessageId = new Map<string, ChatHighlight[]>()

    for (const highlight of chatHighlights) {
      const messageHighlights = byMessageId.get(highlight.messageId) ?? []
      messageHighlights.push(highlight)
      byMessageId.set(highlight.messageId, messageHighlights)
    }

    return byMessageId
  }, [chatHighlights])

  const lastVisibleUserIndex = useMemo(() => {
    for (let index = visibleMessages.length - 1; index >= 0; index -= 1) {
      if (visibleMessages[index]?.message.role === 'user') {
        return index
      }
    }

    return -1
  }, [visibleMessages])

  const selectedMessageIds = useMemo(
    () => new Set(selectedMessages),
    [selectedMessages]
  )

  // Reset visible count when switching chats
  useEffect(() => {
    setVisibleMessageCount(INITIAL_MESSAGE_LIMIT)
    setIsLoadingMore(false)
    setExhaustedOldestCursor(null)
  }, [scopedChatId])

  // Keep a persisted running assistant turn visible even when later group-chat
  // messages arrive while Losi is still working.
  useEffect(() => {
    if (!pendingAssistantMessageId || sortedMessages.length === 0) return

    const pendingIndex = sortedMessages.findIndex(
      (chatMessage) => chatMessage.message.id === pendingAssistantMessageId
    )
    if (pendingIndex < 0) return

    const neededVisible = sortedMessages.length - pendingIndex
    if (visibleMessageCount < neededVisible) {
      setVisibleMessageCount(neededVisible)
    }
  }, [pendingAssistantMessageId, sortedMessages, visibleMessageCount])

  // If a highlighted message is requested, ensure it is within the visible window
  useEffect(() => {
    if (!highlightedMessageId || sortedMessages.length === 0) return

    const idx = sortedMessages.findIndex((m) => m.message.id === highlightedMessageId)
    if (idx >= 0) {
      const neededVisible = sortedMessages.length - idx
      if (visibleMessageCount < neededVisible) {
        setVisibleMessageCount(neededVisible)
      }
    }
  }, [highlightedMessageId, sortedMessages, visibleMessageCount])

  // Scroll to highlighted message once it is in the visible list; use DOM id so scroll works reliably
  const highlightedIsVisible = useMemo(
    () => Boolean(highlightedMessageId && visibleMessages.some((m) => m.message.id === highlightedMessageId)),
    [highlightedMessageId, visibleMessages]
  )
  useEffect(() => {
    if (!highlightedMessageId || !highlightedIsVisible) return
    setShouldScrollToHighlight(true)
    const timeoutId = setTimeout(() => {
      const el = document.getElementById(`message-${highlightedMessageId}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else {
        scrollToMessage(highlightedMessageRef)
      }
    }, 200)
    return () => clearTimeout(timeoutId)
  }, [highlightedMessageId, highlightedIsVisible, setShouldScrollToHighlight, scrollToMessage])

  const [pendingHighlightOpen, setPendingHighlightOpen] =
    useState<ChatHighlightOpenDetail | null>(null)

  useEffect(() => {
    const handleHighlightOpen = (event: Event) => {
      const detail = (event as CustomEvent<ChatHighlightOpenDetail>).detail
      if (!detail?.highlightId || !detail.messageId) return

      const idx = sortedMessages.findIndex(
        (chatMessage) => chatMessage.message.id === detail.messageId
      )
      if (idx < 0) return

      const neededVisible = sortedMessages.length - idx
      if (visibleMessageCount < neededVisible) {
        setVisibleMessageCount(neededVisible)
      }

      setShouldScrollToHighlight(true)
      setPendingHighlightOpen(detail)
    }

    window.addEventListener(CHAT_HIGHLIGHT_OPEN_EVENT, handleHighlightOpen)
    return () => {
      window.removeEventListener(CHAT_HIGHLIGHT_OPEN_EVENT, handleHighlightOpen)
    }
  }, [setShouldScrollToHighlight, sortedMessages, visibleMessageCount])

  useEffect(() => {
    if (!pendingHighlightOpen) return

    const isVisible = visibleMessages.some(
      (chatMessage) => chatMessage.message.id === pendingHighlightOpen.messageId
    )
    if (!isVisible) return

    let timeoutId: number | null = null
    let attempts = 0
    const scrollToHighlight = () => {
      const highlightElement = document.querySelector(
        `[data-chat-highlight-id="${pendingHighlightOpen.highlightId}"]`
      )
      const messageElement = document.getElementById(
        `message-${pendingHighlightOpen.messageId}`
      )
      const scrollTarget = highlightElement ?? messageElement

      if (scrollTarget || attempts >= 6) {
        scrollTarget?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setPendingHighlightOpen(null)
        return
      }

      attempts += 1
      timeoutId = window.setTimeout(scrollToHighlight, 80)
    }

    timeoutId = window.setTimeout(scrollToHighlight, 120)

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [pendingHighlightOpen, visibleMessages])

  const { handleSendEdit, handleSendMessage } = useChatHandler({
    chatId: scopedChatId ?? undefined
  })

  // Load more messages when "Load More" is clicked
  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMoreMessages) return

    setIsLoadingMore(true)

    try {
      if (localHasMoreMessages) {
        setVisibleMessageCount((currentCount) =>
          Math.min(currentCount + LOAD_MORE_BATCH_SIZE, sortedMessages.length)
        )
        return
      }

      if (!scopedChatId || !oldestLoadedCreatedAt) {
        return
      }

      const fetchedMessages = (await getMessagesByChatId(
        scopedChatId,
        LOAD_MORE_BATCH_SIZE + 1,
        { before: oldestLoadedCreatedAt }
      )) as MessageRowWithFileItems[]

      if (fetchedMessages.length === 0) {
        setExhaustedOldestCursor(oldestLoadedCreatedAt)
        return
      }

      const hasMoreRemoteMessages =
        fetchedMessages.length > LOAD_MORE_BATCH_SIZE
      const pageMessages = hasMoreRemoteMessages
        ? fetchedMessages.slice(-LOAD_MORE_BATCH_SIZE)
        : fetchedMessages
      const parsedMessages = pageMessages
        .map((message) => ({
          ...message,
          metadata: message.metadata ?? null,
          type: message.type ?? null
        }))
        .map(parseDBMessageCodeBlocksAndContent)
      const fetchedFileItems = pageMessages.flatMap(
        (message) => message.file_items ?? []
      )

      if (fetchedFileItems.length > 0) {
        setChatFileItems((currentFileItems) =>
          mergeFileItemsById(currentFileItems, fetchedFileItems)
        )
      }

      setChatMessages((currentMessages) =>
        mergeChatMessagesById(currentMessages, parsedMessages)
      )
      setVisibleMessageCount((currentCount) => currentCount + parsedMessages.length)

      if (!hasMoreRemoteMessages) {
        setExhaustedOldestCursor(pageMessages[0]?.created_at ?? oldestLoadedCreatedAt)
      }
    } catch (error) {
      console.error('Error loading more messages:', error)
    } finally {
      setIsLoadingMore(false)
    }
  }

  async function handleRegenerate(request?: RegenerateRequest) {
    setIsGenerating(true)

    const message = getRegenerateMessage(
      request,
      getPreviousUserMessageContent(sortedMessages)
    )
    const options = getRegenerateSendOptions(request, chatSettings)

    if (onRegenerateMessage) {
      await onRegenerateMessage(message, chatMessages, options)
      return
    }

    await handleSendMessage(message, chatMessages, true, options)
  }

  const [editingMessage, setEditingMessage] = useState<Tables<'messages'>>()

  return (
    <>
      {/* Load More Button - Show at top if there are more messages */}
      {hasMoreMessages && (
        <div className="flex justify-center py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="flex items-center gap-2"
          >
            {isLoadingMore ? (
              <>
                <IconLoader2 className="size-4 animate-spin" />
                {t('chat.loading_messages')}
              </>
            ) : (
              <>
                <IconChevronUp className="size-4" />
                {t('chat.load_more_messages')} ({displayedOlderMessageCount}{' '}
                {t('chat.older_messages')})
              </>
            )}
          </Button>
        </div>
      )}

      {/* Render visible messages */}
      {visibleMessages.map((chatMessage, index, array) => {
        const isHighlighted = chatMessage.message.id === highlightedMessageId
        const isLastUser = index === lastVisibleUserIndex
        const isLastVisibleMessage = index === array.length - 1
        const isPersistedPendingAssistantTurn =
          pendingAssistantMessageId === chatMessage.message.id
        const isActiveAssistantTurn =
          chatMessage.message.role === 'assistant' &&
          (isPersistedPendingAssistantTurn ||
            (isGenerating && isLastVisibleMessage))
        const messageIsGenerating = isActiveAssistantTurn
        const messageFirstTokenReceived =
          isPersistedPendingAssistantTurn && pendingAssistantTurn
            ? pendingAssistantTurn.firstTokenReceived
            : firstTokenReceived
        const previousUserMessageContent =
          chatMessage.message.role === 'assistant' && isLastVisibleMessage
            ? getPreviousUserMessageContent(array.slice(0, index))
            : null

        return (
          <MemoizedMessage
            isGenerating={messageIsGenerating}
            setIsGenerating={setIsGenerating}
            firstTokenReceived={messageFirstTokenReceived}
            isActiveAssistantTurn={isActiveAssistantTurn}
            assistants={assistants}
            key={getChatMessageRenderKey(chatMessage)}
            ref={
              isHighlighted
                ? highlightedMessageRef
                : isActiveAssistantTurn
                  ? activeAssistantMessageRef
                  : isLastUser
                    ? lastUserMessageRef
                    : null
            }
            className={isHighlighted ? 'highlighted-message' : ''}
            codeBlocks={chatMessage.codeBlocks}
            profile={profile}
            allModels={allModels}
            selectedAssistant={selectedAssistant}
            files={files}
            message={chatMessage.message as ExtendedMessage}
            fileItems={visibleMessageFileItems.get(chatMessage.message.id) ?? EMPTY_FILE_ITEMS}
            highlights={highlightsByMessageId.get(chatMessage.message.id) ?? EMPTY_HIGHLIGHTS}
            onCreateHighlight={onCreateHighlight}
            isEditing={editingMessage?.id === chatMessage.message.id}
            isLast={index === array.length - 1}
            onStartEdit={
              isLastUser && chatMessage.message.role === 'user'
                ? setEditingMessage
                : undefined
            }
            onCancelEdit={() => setEditingMessage(undefined)}
            onSubmitEdit={handleSendEdit}
            onSelectCodeBlock={onSelectCodeBlock}
            onSelectMessage={onSelectMessage}
            onRegenerate={handleRegenerate}
            showResponseTime={false}
            isExperimentalCodeEditor={isExperimentalCodeEditor}
            isSelectionMode={isSelecting}
            isSelected={selectedMessageIds.has(chatMessage.message.id)}
            onSelect={handleMessageSelect}
            isPublic={isPublic}
            previousUserMessageContent={previousUserMessageContent}
            highlightQuoteText={isHighlighted ? highlightQuoteText ?? undefined : undefined}
            isSidekick={isSidekick}
            reactionTargetType={resolvedReactionTargetType}
            onConfirmInteractive={(payload) => {
              if (onConfirmInteractiveMessage) {
                void onConfirmInteractiveMessage(payload, chatMessages)
                return
              }

              void handleSendMessage(payload, chatMessages, false)
            }}
          />
        )
      })}
    </>
  )
}
